import { useCallback, useMemo } from 'react';
import {
  FieldExtensionComponentProps,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { CredentialPreflight } from '../../../components/preflight/CredentialPreflight';
import { runPreflightCheck } from './api';
import { PreflightRequest } from './types';

const FIELD_MAPPING_KEY = 'fieldMapping';

const DEFAULT_FIELD_MAPPING = {
  ghClientId: 'ghAppClientId',
  ghInstallationId: 'ghAppInstallationId',
  ghPrivateKey: 'ghAppKey',
  repositories: 'githubRepositories',
  llmClientId: 'llmhubClientId',
  llmClientSecret: 'llmhubClientSecret',
  llmRoleArn: 'llmhubRoleArn',
  llmTenantId: 'llmHubTenantId',
  llmResource: 'llmHubResource',
  awsRegion: 'awsRegion',
  model: 'pandaAgentModel',
} as const;

type FieldMapping = Partial<Record<keyof typeof DEFAULT_FIELD_MAPPING, string>>;

const VERIFIED_MARKER = 'verified';
const UNVERIFIED_MARKER = '';

const getByPath = (source: unknown, path: string): unknown => {
  if (!path) return undefined;
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, source);
};

const asString = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map(v => {
      if (typeof v === 'string') return v.trim();
      if (v && typeof v === 'object' && 'name' in v) {
        const name = (v as { name?: unknown }).name;
        return typeof name === 'string' ? name.trim() : '';
      }
      return '';
    })
    .filter(v => v.length > 0);
};

const readFromSources = (sources: unknown[], path: string): unknown => {
  for (const source of sources) {
    const value = getByPath(source, path);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

const buildRequest = (
  sources: unknown[],
  mapping: FieldMapping,
): PreflightRequest => {
  const resolved: Required<FieldMapping> = { ...DEFAULT_FIELD_MAPPING, ...mapping };
  const read = (key: keyof typeof DEFAULT_FIELD_MAPPING) =>
    readFromSources(sources, resolved[key]);
  return {
    githubApp: {
      clientId: asString(read('ghClientId')).trim(),
      installationId: asString(read('ghInstallationId')).trim(),
      privateKey: asString(read('ghPrivateKey')),
    },
    llmHub: {
      clientId: asString(read('llmClientId')).trim(),
      clientSecret: asString(read('llmClientSecret')),
      roleArn: asString(read('llmRoleArn')).trim(),
      tenantId: asString(read('llmTenantId')).trim() || undefined,
      resource: asString(read('llmResource')).trim() || undefined,
      region: asString(read('awsRegion')).trim() || undefined,
      model: asString(read('model')).trim() || undefined,
    },
    repositories: asStringArray(read('repositories')),
  };
};

export const PandaPreflightField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, formData, formContext, uiSchema, rawErrors } = props;
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const { secrets } = useTemplateSecrets();

  const mapping = useMemo<FieldMapping>(
    () => (uiSchema?.['ui:options']?.[FIELD_MAPPING_KEY] as FieldMapping) ?? {},
    [uiSchema],
  );

  // Secrets bag first: `ui:field: Secret` inputs (ghAppKey, llmhubClientSecret)
  // hold the real value there; formData only has a masked '****'.
  const request = useMemo(
    () => buildRequest([secrets, formContext?.formData], mapping),
    [secrets, formContext, mapping],
  );
  const signature = useMemo(() => JSON.stringify(request), [request]);

  const onVerify = useCallback(
    () => runPreflightCheck(discovery, fetchApi, request),
    [discovery, fetchApi, request],
  );
  // The field only satisfies its `^verified$` schema once every check passed,
  // which blocks the wizard from advancing until the credentials are valid.
  const onResult = useCallback(
    (result: { ok: boolean }) =>
      onChange(result.ok ? VERIFIED_MARKER : UNVERIFIED_MARKER),
    [onChange],
  );
  // A credential change (or a revisit) invalidates a prior pass.
  const onReset = useCallback(() => onChange(UNVERIFIED_MARKER), [onChange]);

  const schemaError =
    (rawErrors ?? []).length > 0 && formData !== VERIFIED_MARKER;

  return (
    <div
      style={{
        background: 'var(--bui-bg-neutral-1)',
        border: '1px solid var(--bui-border-1)',
        borderRadius: 'var(--bui-radius-3)',
        padding: '16px 18px',
      }}
    >
      <CredentialPreflight
        onVerify={onVerify}
        onResult={onResult}
        onReset={onReset}
        resetSignal={signature}
        title="Pre-flight credential check"
        description="Replays the exact GitHub App and LLM Hub authentication chains PANDA uses at runtime. All checks must pass before you can continue."
      />
      {schemaError && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--bui-fg-danger)' }}>
          Run the verification before you can continue.
        </div>
      )}
    </div>
  );
};
